import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from './apiBase';
import {
  EMPTY_EXCEPTION_FILTERS,
  filterExceptionRows,
  hasActiveExceptionFilters,
} from './reconExceptionFilters';

/** Selects apply immediately; text/amount fields apply on Apply or Enter. */
const INSTANT_FILTER_KEYS = new Set(['category', 'element', 'metric']);

/**
 * @param {{ enabled: boolean, refreshKey?: number, metric?: 'exceptions'|'matched' }} options
 */
export function useExceptionFilters({ enabled, refreshKey = 0, metric = 'exceptions' }) {
  const [filters, setFilters] = useState({ ...EMPTY_EXCEPTION_FILTERS });
  const [applied, setApplied] = useState({ ...EMPTY_EXCEPTION_FILTERS });
  const [filterOptions, setFilterOptions] = useState(null);

  const clearAllFilters = useCallback(() => {
    setFilters({ ...EMPTY_EXCEPTION_FILTERS });
    setApplied({ ...EMPTY_EXCEPTION_FILTERS });
  }, []);

  const applyFilters = useCallback(() => {
    setApplied({ ...filters });
  }, [filters]);

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      if (INSTANT_FILTER_KEYS.has(key)) {
        setApplied(next);
      }
      return next;
    });
  }, []);

  const patchFilters = useCallback((patch) => {
    setFilters((prev) => {
      const next = { ...prev, ...patch };
      if (Object.keys(patch).some((k) => INSTANT_FILTER_KEYS.has(k))) {
        setApplied(next);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    if (!enabled) {
      setFilterOptions(null);
      return;
    }
    const q = metric && metric !== 'exceptions' ? `?metric=${encodeURIComponent(metric)}` : '';
    apiFetch(`/api/recon/exception-filters${q}`)
      .then(setFilterOptions)
      .catch(() => setFilterOptions(null));
  }, [enabled, refreshKey, metric]);

  useEffect(() => {
    if (!enabled) return;
    clearAllFilters();
  }, [enabled, refreshKey, metric, clearAllFilters]);

  /** Ensure displayed rows always match applied filters (guards stale API pages). */
  const refineRows = useCallback(
    (rows) => filterExceptionRows(rows || [], applied),
    [applied]
  );

  const hasPendingApply = JSON.stringify(filters) !== JSON.stringify(applied);
  const hasActiveFilters = hasActiveExceptionFilters(applied);

  return {
    filters,
    applied,
    filterOptions,
    updateFilter,
    patchFilters,
    setFilters,
    applyFilters,
    clearAllFilters,
    refineRows,
    hasPendingApply,
    hasActiveFilters,
  };
}
